import React, { useState, useContext } from 'react';
import styled from 'styled-components/macro';

import { fontDefault, fontkoKR, IconVolumeOn } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';
import { classes } from '@mirinae/react-ui';
import GTM from '@mirinae/apis/GoogleAnalytics';

import { LessonButton, lessonsPalette, zIndex } from '@mirinae/lessons/modules/defines/styles';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { ButtonStyle } from './WordCard';
import { getFields } from '@mirinae/lessons/modules/libs/elementLib';
import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';
import { playAzureTTS, playTTS } from '@mirinae/lessons/modules/libs/lessonLib';
import { azureVoicings, voicings } from '@mirinae/lessons/components/lesson/elements/LetterCard';

const Stage = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  
    .${classes.desktop} & {
        flex-direction: row;
        justify-content: center;
    }
`;

const TargetArea = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 274px;
    height: 100%;
`;

const TargetWord = styled.div`
    font-family: ${fontkoKR};
    color: #3C3C3B;
    font-size: 80px;
    font-style: normal;
    font-weight: 500;
  
    // ${os.isOthers ? `
    //     padding-right: 60px;
    // ` : `
    //     padding-right: 25px;
    // `}
`;

const ButtonsBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    width: 346px;
`;

const ChoiceButton = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 165px;
    height: 90px;
    background-color: ${lessonsPalette.primary.white};
    border: 1px solid ${lessonsPalette.grey[5]};
    box-sizing: border-box;
    box-shadow: 3px 3px 7px rgba(159, 188, 186, 0.4);
    border-radius: 10px;
    color: ${lessonsPalette.grey[0]};
    cursor: pointer;
    & span {
        color: inherit !important;
    }
    &:hover {
        background-color: ${lessonsPalette.lessons[4]};
        border-color: ${lessonsPalette.darken[0]};
        box-shadow: none;
    }
`;

const BadChoiceButton = styled(ChoiceButton)`
    border-color: ${lessonsPalette.lessons[2]};
    background-color: ${lessonsPalette.lessons[3]};
    animation: wiggle 0.25s;
    box-shadow: none;

    &:hover {
        border-color: ${lessonsPalette.lessons[2]};
        background-color: ${lessonsPalette.lessons[3]};
    }

    @keyframes wiggle {
        0% { transform: rotate(0deg); }
        5% { transform: rotate(-5deg); }
       80% { transform: rotate(5deg); }
      100% { transform: rotate(0deg); }
    }
`;

const CorrectChoiceButton = styled(ChoiceButton)`
    background: ${lessonsPalette.lessons[4]};
    border-color: ${lessonsPalette.primary.main};
    box-shadow: none;
`;

const HiddenButton = styled(ChoiceButton)`
    visibility: hidden;
`;

const ChoiceButtonLabel = styled.div`
    font-family: ${fontDefault};
    font-size: 22px;
    font-weight: 400;
`;

export const PlayAudioButton = styled(LessonButton)`
    z-index: ${zIndex.float + 1};
    ${ButtonStyle};
    left: 56px;
    top: 76px;
    border: none;
    transform: scale(2.7);
`;

const PickALetter = ({ element }) => {
    const [audioEl, setAudioEl] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [badPick, setBadPick] = useState(null);
    const [complete, setComplete] = useState(false);
    const player = useContext(PlayerContext);

    const { correctAnswer: correctAnswerButton } = getFields(element, 'correctAnswer');
    const correctAnswerIndex = parseInt(correctAnswerButton.split(' ')[1], 10);

    const buttonClicked = (i) => {
        setBadPick(null);

        if (i === correctAnswerIndex) {
            setComplete(true);
            player.postEvent({ action: 'complete' }, element);
            GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
        } else {
            setBadPick(i);
            GTM.userAction('error', { page: player.currentPage.componentName }, 'lessons');
            player.postEvent({ action: 'error' }, element);
        }
    };

    const playAudio = (e) => {
        setPlaying(true);
        // playTTS(audioEl, element.fieldsByName.letter.value, e, voicings[element.fieldsByName?.voicing?.value || 'Male slow']);
        playAzureTTS(audioEl, element.fieldsByName.target.value, e, azureVoicings[element.fieldsByName?.voicing?.value || 'Female slow']);
        e.stopPropagation();
        GTM.userAction('word-audio', { word: element.fieldsByName.target.value }, 'lessons');
    };

    const buttonIsHidden = (i) => element.fieldsByName[`button${i}`].hiddenParts && element.fieldsByName[`button${i}`].hiddenParts.length > 0;

    return (
        <Element element={element}>
            <Stage>
                <TargetArea>
                    { complete && <SuccessOverlay top={os.isOthers ? -55 : -30} left={1} height={`${os.isOthers ? 300: 600}px`} /> }
                    { !element.fieldsByName.type || element.fieldsByName.type.value === 'Pick by reading' ? (
                        <TargetWord>
                            {element.fieldsByName.target.value}
                        </TargetWord>
                    ) : (
                        <PlayAudioButton type="button" onClick={playAudio}><IconVolumeOn pointerEvents="all" color={playing ? '#48A9A6' : '#525252'} hoverColor="#48A9A6" /></PlayAudioButton>
                    )}
                </TargetArea>
                <ButtonsBox>
                    { [1, 2, 3, 4].filter(i => !buttonIsHidden(i)).map(i => {
                        let Button = badPick === i ? BadChoiceButton : ChoiceButton;
                        if (complete) {
                            if (i === correctAnswerIndex) {
                                Button = CorrectChoiceButton;
                            } else {
                                Button = HiddenButton;
                            }
                        }

                        return element.fieldsByName[`button${i}`] && (
                            <Button data-t={`pickaletter-btn-${i}`} key={i} onClick={() => buttonClicked(i)}>
                                <ChoiceButtonLabel>{element.fieldsByName[`button${i}`].value}</ChoiceButtonLabel>
                            </Button>
                        );
                    })}
                </ButtonsBox>
            </Stage>
            <audio
                ref={setAudioEl}
                onEnded={() => setPlaying(false)}
            />
        </Element>
    );
};

export default PickALetter;
